/**
 * useReviewPlayer — local replay state machine.
 *
 * Drives step index, play/pause, and speed for the hand-replay route.
 * Uses useReducer so tests can exercise transitions without mounting
 * the component. setInterval auto-advances while playing; reaching
 * the final frame auto-pauses.
 */
import { useCallback, useEffect, useReducer } from 'react';

export type Speed = 0.5 | 1 | 2 | 4;
export const SPEED_OPTIONS: ReadonlyArray<Speed> = [0.5, 1, 2, 4];

const BASE_MS_PER_STEP = 600;

export type ReviewPlayerState = {
  step: number;
  totalSteps: number;
  playing: boolean;
  speed: Speed;
};

type Action =
  | { type: 'setTotal'; totalSteps: number }
  | { type: 'setStep'; step: number }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'toggle' }
  | { type: 'setSpeed'; speed: Speed }
  | { type: 'cycleSpeed'; direction: 1 | -1 };

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function reducer(state: ReviewPlayerState, action: Action): ReviewPlayerState {
  switch (action.type) {
    case 'setTotal':
      return {
        ...state,
        totalSteps: action.totalSteps,
        step: clamp(state.step, 0, Math.max(0, action.totalSteps - 1)),
      };
    case 'setStep':
      return { ...state, step: clamp(action.step, 0, Math.max(0, state.totalSteps - 1)) };
    case 'next': {
      const next = clamp(state.step + 1, 0, Math.max(0, state.totalSteps - 1));
      return {
        ...state,
        step: next,
        playing: state.playing && next < state.totalSteps - 1,
      };
    }
    case 'prev':
      return { ...state, step: clamp(state.step - 1, 0, Math.max(0, state.totalSteps - 1)) };
    case 'play':
      // Don't auto-restart past the end; caller can manually reset.
      if (state.step >= state.totalSteps - 1) return state;
      return { ...state, playing: true };
    case 'pause':
      return { ...state, playing: false };
    case 'toggle':
      return state.playing ? { ...state, playing: false } : reducer(state, { type: 'play' });
    case 'setSpeed':
      return { ...state, speed: action.speed };
    case 'cycleSpeed': {
      const idx = SPEED_OPTIONS.indexOf(state.speed);
      const nextIdx = clamp(idx + action.direction, 0, SPEED_OPTIONS.length - 1);
      return { ...state, speed: SPEED_OPTIONS[nextIdx] ?? state.speed };
    }
  }
}

export function msPerStep(speed: Speed): number {
  return BASE_MS_PER_STEP / speed;
}

export type UseReviewPlayer = ReviewPlayerState & {
  setStep: (step: number) => void;
  setTotal: (total: number) => void;
  next: () => void;
  prev: () => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setSpeed: (s: Speed) => void;
  cycleSpeed: (direction: 1 | -1) => void;
};

export function useReviewPlayer(initialTotal: number): UseReviewPlayer {
  const [state, dispatch] = useReducer(reducer, {
    step: 0,
    totalSteps: initialTotal,
    playing: false,
    speed: 1 as Speed,
  });

  useEffect(() => {
    dispatch({ type: 'setTotal', totalSteps: initialTotal });
  }, [initialTotal]);

  // Auto-advance while playing.
  useEffect(() => {
    if (!state.playing) return;
    if (state.totalSteps <= 1) return;
    const id = window.setInterval(() => {
      dispatch({ type: 'next' });
    }, msPerStep(state.speed));
    return () => window.clearInterval(id);
  }, [state.playing, state.speed, state.totalSteps]);

  return {
    ...state,
    setStep: useCallback((step: number) => dispatch({ type: 'setStep', step }), []),
    setTotal: useCallback((total: number) => dispatch({ type: 'setTotal', totalSteps: total }), []),
    next: useCallback(() => dispatch({ type: 'next' }), []),
    prev: useCallback(() => dispatch({ type: 'prev' }), []),
    play: useCallback(() => dispatch({ type: 'play' }), []),
    pause: useCallback(() => dispatch({ type: 'pause' }), []),
    toggle: useCallback(() => dispatch({ type: 'toggle' }), []),
    setSpeed: useCallback((s: Speed) => dispatch({ type: 'setSpeed', speed: s }), []),
    cycleSpeed: useCallback((direction: 1 | -1) => dispatch({ type: 'cycleSpeed', direction }), []),
  };
}
